import { CommonModule } from '@angular/common';
import { Component, computed, effect, ElementRef, inject, input, output, signal, Type } from '@angular/core';
import { LessonStatus } from '@lb/api/mylb/enums';
import { IEnrollmentDetailItem, ILessonListItem } from '@lb/api/mylb/interfaces';
import { TimezoneAwareDatePipe } from '@lb/pipes';
import {
	MDButtonGroupComponent,
	MDButtonToggleComponent,
	MDContentBoxComponent,
	MDDialogService,
	MDIconComponent,
	MDTagComponent,
	MDToolTipDirective
} from '@melodic-ui/components';
import { LoaderComponent } from '../loader/loader.component';
import { getLessonItemStatus, getLessonStatusConfig, LessonItemStatus } from '../lessons/utils/lesson-status-config';
import { getSemesterMilestoneIcon } from '../lessons/utils/semester-milestone-config';
import { EnrollmentTimelineV2LegendDialogComponent } from './enrollment-timeline-legend-dialog/enrollment-timeline-legend-dialog.component';

export type SemesterKey = number | 'none';

/**
 * A lesson waiting on someone — a new time (Pending Reschedule) or a payment (Awaiting Payment). Decided by status,
 * not by a missing semester: the server keeps waiting lessons in a semester (at the end of the enrollment).
 */
function isNeedsAttention(lesson: ILessonListItem): boolean {
	return lesson.lessonStatusID === LessonStatus.PendingReschedule || lesson.lessonStatusID === LessonStatus.AwaitingPayment;
}

interface ITimelineV2Item {
	lesson: ILessonListItem;
	number: number | null;
	label: string;
	time: string;
	/** Lesson-type icon shown on the chip below the dot. */
	lessonTypeIcon: string;
	/** Status icon shown in the middle of the dot, sourced from lesson-status-config. */
	statusIcon: string;
	/** Visual status variant — drives the dot's color modifier + icon-color classes. */
	status: LessonItemStatus;
	isCurrent: boolean;
	isTodayOrNext: boolean;
	/** Precomputed dot tooltip (omits the date for needs-attention items). */
	tooltip: string;
	/**
	 * Waiting on someone (Pending Reschedule / Awaiting Payment). Its date is a placeholder, so it sits at the end of
	 * its semester's track with no date, and it is also listed in the Needs Attention tab.
	 */
	isNeedsAttention: boolean;
	/** Whether this item renders its date label above the dot (anchored to the first/last real lesson). */
	showDateLabel: boolean;
}

interface ISemesterV2Group {
	key: SemesterKey;
	label: string | null;
	items: ITimelineV2Item[];
}

@Component({
	selector: 'lb-enrollment-timeline',
	standalone: true,
	imports: [
		CommonModule,
		MDContentBoxComponent,
		MDIconComponent,
		MDTagComponent,
		MDToolTipDirective,
		MDButtonGroupComponent,
		MDButtonToggleComponent,
		LoaderComponent
	],
	templateUrl: './enrollment-timeline.component.html',
	styleUrl: './enrollment-timeline.component.scss'
})
export class EnrollmentTimelineComponent {
	private readonly datePipe = inject(TimezoneAwareDatePipe);
	private readonly elementRef = inject(ElementRef<HTMLElement>);
	private readonly _dialogService = inject(MDDialogService);

	enrollment = input.required<IEnrollmentDetailItem>();
	lessons = input<ILessonListItem[]>([]);
	relatedLessonsLoading = input<boolean>(false);
	userView = input<'learner' | 'instructor'>('instructor');
	activeLessonID = input<number | null>(null);

	lessonSelect = output<number>();
	semesterChange = output<number>();

	activeSemesterKey = signal<SemesterKey | null>(null);
	loadingRelatedLessons = computed(() => this.relatedLessonsLoading());

	todayOrNextLesson = computed(() => {
		// A waiting lesson's date is a placeholder, never "today" or "next".
		const sorted = [...this.lessons()]
			.filter((lesson) => lesson.lessonStatusID !== 5 && !isNeedsAttention(lesson))
			.sort((a, b) => new Date(a.lessonDate).getTime() - new Date(b.lessonDate).getTime());
		if (sorted.length === 0) {
			return { lessonID: null as number | null };
		}

		const now = new Date();
		const todayLesson = sorted.find((lesson) => this.isSameLocalDay(new Date(lesson.lessonDate), now));
		if (todayLesson) {
			return { lessonID: todayLesson.lessonID };
		}

		const nextLesson = sorted.find((lesson) => new Date(lesson.lessonDate).getTime() >= now.getTime());
		if (nextLesson) {
			return { lessonID: nextLesson.lessonID };
		}

		return { lessonID: null as number | null };
	});

	timelineItems = computed((): ITimelineV2Item[] => {
		const sorted = [...this.lessons()].sort((a, b) => new Date(a.lessonDate).getTime() - new Date(b.lessonDate).getTime());
		const todayOrNext = this.todayOrNextLesson();
		return sorted.map((lesson) => {
			// Server-computed over the whole enrollment (ILessonListItem.lessonNumber).
			const lessonNumber = lesson.lessonNumber ?? null;
			const label = this.datePipe.transform(lesson.lessonDate, 'MMM d, y') ?? '';
			const time = this.datePipe.transform(lesson.lessonDate, 'hh:mm a') ?? '';
			const needsAttention = isNeedsAttention(lesson);
			return {
				lesson,
				number: lessonNumber,
				label,
				time,
				status: getLessonItemStatus(lesson, this.userView()),
				isCurrent: lesson.lessonID === this.activeLessonID(),
				isTodayOrNext: lesson.lessonID === todayOrNext.lessonID,
				statusIcon: getLessonStatusConfig(lesson, this.userView()).icon,
				lessonTypeIcon: this.getLessonTypeIcon(lesson.lessonTypeID),
				// Needs-attention items omit the (stale) date; show the status reason + lesson type instead.
				tooltip: needsAttention ? `${this.getReviewReason(lesson)} · ${lesson.lessonTypeName}` : `${label} ${time} · ${lesson.lessonTypeName}`,
				isNeedsAttention: needsAttention,
				// Anchored to the first/last real lesson in semesterGroups(); default false.
				showDateLabel: false
			};
		});
	});

	semesterGroups = computed((): ISemesterV2Group[] => {
		const items = this.timelineItems();
		if (items.length === 0) {
			return [];
		}

		// Semester tracks: every lesson with a semester. The server places waiting lessons (Pending Reschedule /
		// Awaiting Payment) in a semester too — at the end of the enrollment — so they show on that track, after its
		// dated lessons, since their own date is only a placeholder.
		const groupMap = new Map<number, ITimelineV2Item[]>();
		for (const item of items) {
			if (item.lesson.semesterID == null) continue;
			if (!groupMap.has(item.lesson.semesterID)) groupMap.set(item.lesson.semesterID, []);
			groupMap.get(item.lesson.semesterID)?.push(item);
		}
		for (const [key, groupItems] of groupMap) {
			groupMap.set(key, [...groupItems.filter((i) => !i.isNeedsAttention), ...groupItems.filter((i) => i.isNeedsAttention)]);
		}

		// Semesters in date order of their first dated lesson; a track holding only waiting lessons goes last.
		const firstDate = (key: number) => {
			const dated = groupMap.get(key)?.find((i) => !i.isNeedsAttention);
			return dated ? new Date(dated.lesson.lessonDate).getTime() : Number.MAX_SAFE_INTEGER;
		};
		const groups: ISemesterV2Group[] = [...groupMap.keys()]
			.sort((a, b) => firstDate(a) - firstDate(b))
			.map((key) => ({
				key,
				label: `Semester ${groupMap.get(key)?.[0]?.lesson.languageLevelName ?? 'Level Pending'}`,
				items: [...(groupMap.get(key) ?? [])]
			}));

		// A lesson with no semester (an enrollment without semesters, or one not resequenced since) still shows on a
		// track: folded into the last one, or a single "Lessons" track when there are no semesters.
		const unplaced = items.filter((i) => i.lesson.semesterID == null);
		if (unplaced.length > 0) {
			if (groups.length === 0) groups.push({ key: 0, label: 'Lessons', items: [] });
			const last = groups[groups.length - 1];
			const byDate = new Map(items.map((item, index) => [item, index]));
			const all = [...last.items, ...unplaced].sort((a, b) => (byDate.get(a) ?? 0) - (byDate.get(b) ?? 0));
			last.items = [...all.filter((i) => !i.isNeedsAttention), ...all.filter((i) => i.isNeedsAttention)];
		}

		// Date labels anchor to the first and last DATED lesson of each track.
		for (const group of groups) {
			const dated = group.items.filter((i) => !i.isNeedsAttention);
			if (dated.length === 0) continue;
			dated[0].showDateLabel = true;
			dated[dated.length - 1].showDateLabel = true;
		}

		// Needs Attention: every waiting lesson, chosen by status, whatever semester it sits in.
		const attention = items.filter((i) => i.isNeedsAttention);
		if (attention.length > 0) {
			groups.push({ key: 'none', label: 'Needs Attention ', items: attention });
		}

		return groups;
	});

	timelineHasSemesters = computed(() => this.semesterGroups().length > 1);

	activeSemesterGroup = computed(() => {
		const groups = this.semesterGroups();
		if (groups.length === 0) return null;
		return groups.find((g) => g.key === this.activeSemesterKey()) ?? groups[0];
	});

	isNeedsReview = computed(() => this.activeSemesterGroup()?.key === 'none');

	constructor() {
		effect(() => {
			const groups = this.semesterGroups();
			if (groups.length === 0) return;
			const currentID = this.activeLessonID();

			if (currentID != null) {
				const matchingGroup = groups.find((g) => g.items.some((i) => i.lesson.lessonID === currentID));
				if (matchingGroup) {
					this.activeSemesterKey.set(matchingGroup.key);
					return;
				}
			}

			if (!groups.some((g) => g.key === this.activeSemesterKey())) {
				this.activeSemesterKey.set(groups[0].key);
			}
		});

		effect(() => {
			const currentID = this.activeLessonID();
			if (currentID == null) return;
			setTimeout(() => {
				const active = this.elementRef.nativeElement.querySelector('.timeline-v2-item--current') as HTMLElement | null;
				active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
			});
		});
	}

	onSemesterTabChange(value: unknown): void {
		const selected = Array.isArray(value) ? value[0] : value;
		if (selected === null || selected === undefined) return;
		const normalized: SemesterKey = selected === 'none' ? 'none' : Number(selected);
		this.selectSemester(normalized);
	}

	selectLesson(lessonID: number): void {
		this.lessonSelect.emit(lessonID);
	}

	selectSemester(key: SemesterKey): void {
		this.activeSemesterKey.set(key);
		if (typeof key === 'number') {
			this.semesterChange.emit(key);
		}
	}

	getLessonTypeIcon(lessonTypeID: number): string {
		switch (lessonTypeID) {
			case 1: // Mastery
				return 'untitled-ui/general/target-05';
			case 2: // Instructional
				return 'untitled-ui/media/monitor-01';
			case 3: // Tutoring
				return 'untitled-ui/communication/message-circle-01';
			case 4: // Chirp Room
				return 'mylb/bird-outline';
			default:
				return 'untitled-ui/education/book-open-01';
		}
	}

	getSemesterLessonTypeIcon(typeID: number): string | null {
		return getSemesterMilestoneIcon(typeID);
	}

	getReviewReason(lesson: ILessonListItem): string {
		return lesson.lessonStatusName ?? 'Unknown';
	}

	openLegend(): void {
		this._dialogService.open(EnrollmentTimelineV2LegendDialogComponent as Type<Component>, {
			width: '480px'
		});
	}

	private isSameLocalDay(a: Date, b: Date): boolean {
		return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
	}
}
